import { FilesetResolver, PoseLandmarker, type PoseLandmarkerResult } from '@mediapipe/tasks-vision';
import { OneEuroVec3 } from '../core/filter';
import { v3, type Vec3 } from '../core/math';
import { LANDMARK_COUNT } from './landmarks';
import { poseQuality, visibilityIsUnavailable } from './quality';
import type { RawPose } from './types';

export type ModelTier = 'lite' | 'full';

export interface PoseSourceOptions {
  tier?: ModelTier;
  /** Requested capture size. 480p is the sweet spot: BlazePose downsamples to 256 anyway. */
  width?: number;
  height?: number;
  fps?: number;
  /** Landmarks below this visibility are treated as missing. */
  visibilityFloor?: number;
  onStatus?: (msg: string) => void;
}

const BASE = import.meta.env.BASE_URL ?? '/';

/**
 * Camera + BlazePose wrapper.
 *
 * Design notes:
 *  - Inference is driven by `requestVideoFrameCallback` when available, so we run
 *    exactly once per *camera* frame instead of once per *display* frame. On a
 *    144 Hz monitor with a 30 fps webcam that is a 4.8x saving with zero loss of
 *    information.
 *  - It deliberately does NOT live in a Web Worker. Getting frames into a worker
 *    costs an `ImageBitmap` copy per frame, and on the machines that need the help
 *    most (integrated GPUs) that copy eats the gain. The worker path is kept as a
 *    documented stretch goal; see DESIGN.md -> "Performance".
 *  - Filtering happens here, before anything else sees the data, so every consumer
 *    shares one consistent smoothing policy.
 */
export class PoseSource {
  private landmarker: PoseLandmarker | null = null;
  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private rvfcHandle = 0;
  private rafHandle = 0;
  private running = false;
  private lastTimestamp = -1;
  private opts: Required<Omit<PoseSourceOptions, 'onStatus'>> & { onStatus: (m: string) => void };

  private imgFilters: OneEuroVec3[] = [];
  private worldFilters: OneEuroVec3[] = [];
  private imgOut: Vec3[] = [];
  private worldOut: Vec3[] = [];
  private visOut: number[] = new Array(LANDMARK_COUNT).fill(0);

  /** Rolling inference cost, ms. Drives the automatic quality ladder. */
  inferenceMs = 0;
  /** Frames the model produced no person for, consecutively. */
  missedFrames = 0;

  /**
   * Diagnostics. Every one of these exists because "it just sits there" is an
   * unanswerable bug report, and the failure can be at any of four independent
   * stages: camera frames, inference calls, pose results, or pose quality.
   */
  readonly diag = {
    /** Camera frames observed by the pump. 0 => rVFC/rAF is not firing. */
    frames: 0,
    /** detectForVideo calls that returned without throwing. */
    detections: 0,
    /** Results that actually contained a person. */
    poses: 0,
    /** Results with no person in them. */
    empty: 0,
    lastQuality: 0,
    lastError: '' as string,
    /** True once we have concluded the model does not populate `visibility`. */
    visibilityUnavailable: false,
  };

  onPose: ((p: RawPose) => void) | null = null;
  onLost: (() => void) | null = null;

  constructor(options: PoseSourceOptions = {}) {
    this.opts = {
      tier: options.tier ?? 'lite',
      width: options.width ?? 640,
      height: options.height ?? 480,
      fps: options.fps ?? 30,
      visibilityFloor: options.visibilityFloor ?? 0.5,
      onStatus: options.onStatus ?? (() => {}),
    };
    for (let i = 0; i < LANDMARK_COUNT; i++) {
      // Image-space landmarks are in [0,1]; world landmarks are in metres. The
      // 1-Euro beta term is velocity-scaled, so it needs different magnitudes.
      this.imgFilters.push(new OneEuroVec3(1.7, 0.6, 1.0));
      this.worldFilters.push(new OneEuroVec3(1.7, 0.02, 1.0));
      this.imgOut.push(v3());
      this.worldOut.push(v3());
    }
  }

  get videoElement(): HTMLVideoElement | null {
    return this.video;
  }

  async init(video: HTMLVideoElement): Promise<void> {
    this.video = video;
    this.opts.onStatus('Requesting camera…');
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: this.opts.width },
        height: { ideal: this.opts.height },
        frameRate: { ideal: this.opts.fps, max: 60 },
        facingMode: 'user',
      },
      audio: false,
    });
    video.srcObject = this.stream;
    video.playsInline = true;
    video.muted = true;
    await video.play();
    await new Promise<void>((res) => {
      if (video.readyState >= 2) return res();
      video.onloadeddata = () => res();
    });

    this.opts.onStatus('Loading runtime…');
    const fileset = await FilesetResolver.forVisionTasks(`${BASE}mediapipe`);
    this.landmarker = await this.createLandmarker(fileset, this.opts.tier);
    this.opts.onStatus('Ready');
  }

  /**
   * Fetch the model ourselves rather than handing MediaPipe a URL, purely so the
   * download can report progress. It is ~6 MB, and on a slow connection the
   * alternative is an unexplained multi-second freeze on a screen that says
   * nothing — which reads as a hang.
   */
  private async fetchModel(tier: ModelTier): Promise<Uint8Array> {
    const url = `${BASE}models/pose_landmarker_${tier}.task`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`model download failed (HTTP ${res.status}) — ${url}`);
    const total = Number(res.headers.get('content-length') ?? 0);
    if (!res.body || !total) {
      this.opts.onStatus('Loading pose model…');
      return new Uint8Array(await res.arrayBuffer());
    }
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let got = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      chunks.push(value);
      got += value.length;
      this.opts.onStatus(`Loading pose model… ${Math.round((got / total) * 100)}%`);
    }
    const out = new Uint8Array(got);
    let off = 0;
    for (const c of chunks) {
      out.set(c, off);
      off += c.length;
    }
    return out;
  }

  private async createLandmarker(fileset: Awaited<ReturnType<typeof FilesetResolver.forVisionTasks>>, tier: ModelTier) {
    const buffer = await this.fetchModel(tier);
    const make = (delegate: 'GPU' | 'CPU') =>
      PoseLandmarker.createFromOptions(fileset, {
        baseOptions: {
          // A fresh copy per attempt: the runtime may take ownership of the
          // buffer, and the GPU attempt can fail after consuming it.
          modelAssetBuffer: buffer.slice(),
          delegate,
        },
        runningMode: 'VIDEO',
        numPoses: 1,
        // Detection/presence thresholds are deliberately low. A boxer in guard
        // occludes their own face and crosses their arms; being strict here makes
        // the skeleton drop out at exactly the moments that matter most.
        minPoseDetectionConfidence: 0.4,
        minPosePresenceConfidence: 0.4,
        minTrackingConfidence: 0.4,
        outputSegmentationMasks: false,
      });
    try {
      return await make('GPU');
    } catch (err) {
      console.warn('[pose] GPU delegate unavailable, falling back to CPU/WASM-SIMD.', err);
      this.opts.onStatus('GPU unavailable — using CPU');
      return await make('CPU');
    }
  }

  /** Swap models at runtime (used by the adaptive quality ladder). */
  async setTier(tier: ModelTier): Promise<void> {
    if (tier === this.opts.tier || !this.landmarker) return;
    const fileset = await FilesetResolver.forVisionTasks(`${BASE}mediapipe`);
    const next = await this.createLandmarker(fileset, tier);
    const old = this.landmarker;
    this.landmarker = next;
    this.opts.tier = tier;
    old.close();
  }

  start(): void {
    if (this.running || !this.video || !this.landmarker) return;
    this.running = true;
    this.pump();
  }

  stop(): void {
    this.running = false;
    const v = this.video as HTMLVideoElement & { cancelVideoFrameCallback?: (h: number) => void };
    if (this.rvfcHandle && v?.cancelVideoFrameCallback) v.cancelVideoFrameCallback(this.rvfcHandle);
    if (this.rafHandle) cancelAnimationFrame(this.rafHandle);
    this.rvfcHandle = 0;
    this.rafHandle = 0;
  }

  dispose(): void {
    this.stop();
    this.stream?.getTracks().forEach((t) => t.stop());
    this.landmarker?.close();
    this.landmarker = null;
    this.stream = null;
  }

  private pump(): void {
    const video = this.video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number;
    };
    if (video.requestVideoFrameCallback) {
      const cb = (now: number) => {
        if (!this.running) return;
        this.diag.frames++;
        // Re-register FIRST, in a try/finally. Previously the re-registration
        // came after detect(), so a single throw anywhere downstream (recogniser,
        // scoring, renderer) silently killed the pipeline forever with no error
        // surfaced anywhere. That is indistinguishable from "the camera never
        // started" and is exactly the sort of failure that wastes an afternoon.
        try {
          this.detect(now);
        } catch (err) {
          this.diag.lastError = String((err as Error)?.message ?? err);
          console.error('[pose] frame handler threw', err);
        } finally {
          this.rvfcHandle = video.requestVideoFrameCallback!(cb);
        }
      };
      this.rvfcHandle = video.requestVideoFrameCallback(cb);
    } else {
      // Safari < 15.4 and some Android browsers: fall back to rAF and dedupe on
      // currentTime so we do not re-run inference on an unchanged frame.
      let lastMediaTime = -1;
      const cb = (now: number) => {
        if (!this.running) return;
        this.rafHandle = requestAnimationFrame(cb);
        if (this.video!.currentTime === lastMediaTime) return;
        lastMediaTime = this.video!.currentTime;
        this.diag.frames++;
        try {
          this.detect(now);
        } catch (err) {
          this.diag.lastError = String((err as Error)?.message ?? err);
          console.error('[pose] frame handler threw', err);
        }
      };
      this.rafHandle = requestAnimationFrame(cb);
    }
  }

  private detect(nowMs: number): void {
    if (!this.landmarker || !this.video || this.video.readyState < 2) return;
    // MediaPipe requires strictly increasing timestamps in VIDEO mode.
    const ts = Math.max(this.lastTimestamp + 1, Math.round(nowMs));
    this.lastTimestamp = ts;

    const t0 = performance.now();
    let result: PoseLandmarkerResult;
    try {
      result = this.landmarker.detectForVideo(this.video, ts);
    } catch (err) {
      console.warn('[pose] detect failed', err);
      return;
    }
    this.inferenceMs = this.inferenceMs * 0.85 + (performance.now() - t0) * 0.15;

    this.diag.detections++;

    const lm = result.landmarks?.[0];
    const wl = result.worldLandmarks?.[0];
    if (!lm || !wl || lm.length < LANDMARK_COUNT) {
      this.diag.empty++;
      if (++this.missedFrames === 8) this.onLost?.();
      return;
    }
    this.missedFrames = 0;
    this.diag.poses++;

    const tSec = nowMs / 1000;
    let visSum = 0;
    for (let i = 0; i < LANDMARK_COUNT; i++) {
      const a = lm[i];
      const b = wl[i];
      const v = a.visibility ?? 0;
      this.visOut[i] = v;
      visSum += v;
      this.imgFilters[i].filter({ x: a.x, y: a.y, z: a.z ?? 0 }, tSec, this.imgOut[i]);
      this.worldFilters[i].filter({ x: b.x, y: b.y, z: b.z ?? 0 }, tSec, this.worldOut[i]);
    }

    // MediaPipe's *Tasks* PoseLandmarker does not populate `visibility` — every
    // landmark comes back as exactly 0, unlike the legacy Solutions API. Gating
    // on it therefore rejected 100% of frames and pinned calibration at 0%
    // forever with no error anywhere. Detect the situation once and fall back to
    // a geometric plausibility check, which is a better signal anyway: it asks
    // "does this look like a torso?" rather than trusting a per-point score.
    if (visibilityIsUnavailable(this.visOut) && visSum <= 1e-6) {
      if (!this.diag.visibilityUnavailable) {
        this.diag.visibilityUnavailable = true;
        console.info('[pose] model does not report landmark visibility; using geometric quality instead.');
      }
      // Give downstream consumers a usable per-landmark confidence rather than
      // zeros, or the skeleton overlay and every `vis`-based check go dark.
      this.visOut.fill(1);
    }

    const quality = poseQuality(
      this.worldOut,
      this.imgOut,
      this.visOut,
      this.opts.visibilityFloor,
      this.diag.visibilityUnavailable,
    );
    this.diag.lastQuality = quality;

    this.onPose?.({
      t: nowMs,
      image: this.imgOut,
      world: this.worldOut,
      vis: this.visOut,
      quality,
    });
  }


  resetFilters(): void {
    for (const f of this.imgFilters) f.reset();
    for (const f of this.worldFilters) f.reset();
  }
}
